import React, { useState, useEffect } from 'react';
import {
    Box, Typography, TextField, Button, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, Paper, Select, MenuItem,
    IconButton, Grid, Pagination, Dialog, DialogTitle, DialogContent,
    DialogActions, CircularProgress
} from '@mui/material';
import { Edit, Delete, Add } from '@mui/icons-material';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
    collection,
    addDoc,
    getDocs,
    updateDoc,
    deleteDoc,
    doc,
    query,
    where,
    serverTimestamp,
} from 'firebase/firestore';
import { db } from '../../firebase';
import Udhariheader from './Udhariheader';

const fmt = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;

const UsaneGhene = () => {
    // main state
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);

    // totals
    const [totals, setTotals] = useState({ totalGiven: 0, totalRepaid: 0, totalRemaining: 0 });

    // search / pagination
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);
    const rowsPerPage = 8;

    // form for main record
    const [form, setForm] = useState({ id: null, nav: '', mobile: '', thikan: '', rakkam: '', dinank: '', method: 'Cash' });

    // jama dialog and form
    const [openJamaDialog, setOpenJamaDialog] = useState(false);
    const [jamaForm, setJamaForm] = useState({ id: null, udhariId: '', rakkam: '', dinank: '', haste: '', method: 'Cash' });
    const [jamaHistory, setJamaHistory] = useState([]);

    // fetch all main + history and compute totals
    const fetchData = async () => {
        setLoading(true);
        try {
            const udhariSnap = await getDocs(collection(db, 'udhariGhene'));
            const udhariList = udhariSnap.docs.map(d => ({ id: d.id, ...d.data() }));

            const jamaSnap = await getDocs(collection(db, 'udhariGheneJamaHistory'));
            const jamaList = jamaSnap.docs.map(d => ({ id: d.id, ...d.data() }));

            // merge
            const merged = udhariList.map(u => {
                const history = jamaList.filter(j => j.udhariId === u.id);
                const totalJama = history.reduce((s, h) => s + Number(h.rakkam || 0), 0);
                const remaining = Number(u.rakkam || 0) - totalJama;
                return { ...u, totalJama, remaining, historyCount: history.length };
            });

            // totals across all
            const totalGiven = merged.reduce((s, m) => s + Number(m.rakkam || 0), 0);
            const totalRepaid = merged.reduce((s, m) => s + Number(m.totalJama || 0), 0);
            const totalRemaining = totalGiven - totalRepaid;

            setData(merged);
            setTotals({ totalGiven, totalRepaid, totalRemaining });
        } catch (err) {
            console.error('fetchData error', err);
            toast.error('डेटा लोड करताना त्रुटी');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    // Save (add/update) main record
    const handleSave = async () => {
        if (!form.nav || !form.rakkam || !form.dinank) {
            toast.error('कृपया नाव, रक्कम आणि दिनांक भरा');
            return;
        }

        try {
            const payload = { ...form };
            delete payload.id;
            payload.rakkam = Number(payload.rakkam);

            if (form.id) {
                await updateDoc(doc(db, 'udhariGhene', form.id), payload);
                toast.info('नोंद अपडेट केली ✅');
            } else {
                await addDoc(collection(db, 'udhariGhene'), {
                    ...payload,
                    createdAt: serverTimestamp()
                });
                toast.success('नवीन नोंद जतन केली ✅');
            }

            setForm({ id: null, nav: '', mobile: '', thikan: '', rakkam: '', dinank: '', method: 'Cash' });
            await fetchData();
            setPage(1);
        } catch (err) {
            console.error('handleSave error', err);
            toast.error('जतन करताना त्रुटी');
        }
    };

    const handleEdit = (row) => {
        // populate main form (ensure rakkam as string/number OK for input)
        setForm({ id: row.id, nav: row.nav || '', mobile: row.mobile || '', thikan: row.thikan || '', rakkam: row.rakkam || '', dinank: row.dinank || '', method: row.method || 'Cash' });
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    // Delete main record (and cascade delete its jama history)
    const handleDelete = async (id) => {
        if (!window.confirm('खरंच हटवायचे आहे का? मुख्य नोंद व तिचे जमा इतिहास हटवला जाईल.')) return;

        try {
            await deleteDoc(doc(db, 'udhariGhene', id));
            const q = query(collection(db, 'udhariGheneJamaHistory'), where('udhariId', '==', id));
            const snap = await getDocs(q);
            await Promise.all(snap.docs.map(d => deleteDoc(doc(db, 'udhariGheneJamaHistory', d.id))));
            toast.warn('नोंद व संबंधित जमा इतिहास हटवले ❌');
            // update local state quickly
            setData(prev => prev.filter(x => x.id !== id));
            await fetchData();
        } catch (err) {
            console.error('handleDelete error', err);
            toast.error('हटवताना त्रुटी');
        }
    };

    // Open jama dialog with history loaded by where query (fast)
    const handleOpenJamaDialog = async (udhari) => {
        setOpenJamaDialog(true);
        setJamaForm({ id: null, udhariId: udhari.id, rakkam: '', dinank: '', haste: '', method: udhari.method || 'Cash' });
        try {
            const q = query(collection(db, 'udhariGheneJamaHistory'), where('udhariId', '==', udhari.id));
            const snap = await getDocs(q);
            const history = snap.docs.map(d => ({ id: d.id, ...d.data() })).sort((a, b) => {
                const ta = a.dinank || a.createdAt?.seconds || 0;
                const tb = b.dinank || b.createdAt?.seconds || 0;
                return ta < tb ? -1 : 1;
            });
            setJamaHistory(history);
        } catch (err) {
            console.error('openJama error', err);
            toast.error('जमा इतिहास लोड करताना त्रुटी');
        }
    };

    // Save or update a jama entry
    const handleSaveJama = async () => {
        if (!jamaForm.rakkam || !jamaForm.dinank || !jamaForm.haste) {
            toast.error('कृपया रक्कम, दिनांक व हस्ते भरा');
            return;
        }

        try {
            const payload = { ...jamaForm };
            delete payload.id;
            payload.rakkam = Number(payload.rakkam);

            if (jamaForm.id) {
                await updateDoc(doc(db, 'udhariGheneJamaHistory', jamaForm.id), payload);
                toast.info('जमा सुधारित केली ✅');
            } else {
                await addDoc(collection(db, 'udhariGheneJamaHistory'), {
                    ...payload,
                    createdAt: serverTimestamp()
                });
                toast.success('जमा जतन केली ✅');
            }

            // reload only the history and main totals
            const udhariId = payload.udhariId;
            // reload history:
            const q = query(collection(db, 'udhariGheneJamaHistory'), where('udhariId', '==', udhariId));
            const snap = await getDocs(q);
            const history = snap.docs.map(d => ({ id: d.id, ...d.data() })).sort((a, b) => {
                const ta = a.dinank || a.createdAt?.seconds || 0;
                const tb = b.dinank || b.createdAt?.seconds || 0;
                return ta < tb ? -1 : 1;
            });
            setJamaHistory(history);
            // refresh main list/totals
            await fetchData();
            // clear jama form (keep udhariId)
            setJamaForm({ id: null, udhariId, rakkam: '', dinank: '', haste: '', method: 'Cash' });
        } catch (err) {
            console.error('handleSaveJama error', err);
            toast.error('जमा जतन/सुधारित करताना त्रुटी');
        }
    };

    // Delete a single jama entry
    const handleDeleteJama = async (jamaId, udhariId) => {
        if (!window.confirm('खरंच ही जमा नोंद हटवायची आहे का?')) return;

        try {
            await deleteDoc(doc(db, 'udhariGheneJamaHistory', jamaId));
            toast.warn('जमा नोंद हटवली ❌');
            // remove from local history immediately
            setJamaHistory(prev => prev.filter(h => h.id !== jamaId));
            // recalc main list
            await fetchData();
        } catch (err) {
            console.error('delete jama err', err);
            toast.error('जमा हटवताना त्रुटी');
        }
    };

    // filtering + pagination
    const filtered = data.filter(d =>
        d.nav?.toString().toLowerCase().includes(search.toLowerCase()) ||
        d.mobile?.toString().toLowerCase().includes(search.toLowerCase()) ||
        d.thikan?.toString().toLowerCase().includes(search.toLowerCase()) ||
        (d.rakkam?.toString() || '').includes(search)
    );
    const pageCount = Math.max(1, Math.ceil(filtered.length / rowsPerPage));
    const displayed = filtered.slice((page - 1) * rowsPerPage, page * rowsPerPage);

    return (
        <Box sx={{ p: 2 }}>
            <Udhariheader />

            {/* Totals */}
            {/* <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} sm={4}>
                    <Paper sx={{ p: 2, textAlign: 'center' }}>
                        <Typography variant="subtitle2">एकूण घेणे</Typography>
                        <Typography variant="h6">{fmt(totals.totalGiven)}</Typography>
                    </Paper>
                </Grid>
                <Grid item xs={12} sm={4}>
                    <Paper sx={{ p: 2, textAlign: 'center' }}>
                        <Typography variant="subtitle2">एकूण जमा</Typography>
                        <Typography variant="h6" color="success.main">{fmt(totals.totalRepaid)}</Typography>
                    </Paper>
                </Grid>
                <Grid item xs={12} sm={4}>
                    <Paper sx={{ p: 2, textAlign: 'center' }}>
                        <Typography variant="subtitle2">एकूण बाकी</Typography>
                        <Typography variant="h6" color="error.main">{fmt(totals.totalRemaining)}</Typography>
                    </Paper>
                </Grid>
            </Grid> */}

            {/* Form */}
            <Paper sx={{ p: 2, mb: 2 }}>
                <Typography variant="h6" sx={{ mb: 1 }}>उसणे घेणे - नोंद</Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12} md={3}>
                        <TextField label="नाव" fullWidth size="small" value={form.nav} onChange={(e) => setForm({ ...form, nav: e.target.value })} />
                    </Grid>
                    <Grid item xs={12} md={2}>
                        <TextField label="मोबाईल (ऐच्छिक)" fullWidth size="small" value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} />
                    </Grid>
                    <Grid item xs={12} md={2}>
                        <TextField label="ठिकाण" fullWidth size="small" value={form.thikan} onChange={(e) => setForm({ ...form, thikan: e.target.value })} />
                    </Grid>
                    <Grid item xs={12} md={2}>
                        <TextField label="रक्कम" type="number" fullWidth size="small" value={form.rakkam} onChange={(e) => setForm({ ...form, rakkam: e.target.value })} />
                    </Grid>
                    <Grid item xs={12} md={2}>
                        <TextField label="दिनांक" type="date" fullWidth size="small" InputLabelProps={{ shrink: true }} value={form.dinank} onChange={(e) => setForm({ ...form, dinank: e.target.value })} />
                    </Grid>
                    <Grid item xs={12} md={1}>
                        <Select fullWidth size="small" value={form.method} onChange={(e) => setForm({ ...form, method: e.target.value })}>
                            <MenuItem value="Cash">Cash</MenuItem>
                            <MenuItem value="Online">Online</MenuItem>
                        </Select>
                    </Grid>
                </Grid>
                <Box sx={{ mt: 2, textAlign: 'right' }}>
                    <Button variant="contained" onClick={handleSave}>{form.id ? 'अपडेट करा' : 'जतन करा'}</Button>
                    <Button sx={{ ml: 1 }} onClick={() => setForm({ id: null, nav: '', mobile: '', thikan: '', rakkam: '', dinank: '', method: 'Cash' })}>रद्द करा</Button>
                </Box>
            </Paper>

            {/* Search */}
            <Box sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
                <TextField fullWidth size="small" placeholder="शोधा नाव/मोबाईल/ठिकाण/रक्कम..." value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} />
            </Box>

            {/* Loading */}
            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                    <CircularProgress />
                </Box>
            ) : (
                <>
                    <TableContainer component={Paper}>
                        <Table>
                            <TableHead>
                                <TableRow sx={{ background: '#f3f3f3' }}>
                                    <TableCell>Sr</TableCell>
                                    <TableCell>नाव</TableCell>
                                    <TableCell>मोबाईल</TableCell>
                                    <TableCell>ठिकाण</TableCell>
                                    <TableCell>रक्कम</TableCell>
                                    <TableCell>एकूण जमा</TableCell>
                                    <TableCell>बाकी</TableCell>
                                    <TableCell>दिनांक</TableCell>
                                    <TableCell>पद्धत</TableCell>
                                    <TableCell>क्रिया</TableCell>
                                </TableRow>
                            </TableHead>

                            <TableBody>
                                {displayed.map((row, idx) => (
                                    <TableRow key={row.id} sx={{ background: row.remaining <= 0 ? '#e8f5e9' : 'inherit' }}>
                                        <TableCell>{(page - 1) * rowsPerPage + idx + 1}</TableCell>
                                        <TableCell>{row.nav}</TableCell>
                                        <TableCell>{row.mobile || '-'}</TableCell>
                                        <TableCell>{row.thikan}</TableCell>
                                        <TableCell>{fmt(row.rakkam)}</TableCell>
                                        <TableCell sx={{ fontWeight: 700, color: 'green' }}>{fmt(row.totalJama)}</TableCell>
                                        <TableCell sx={{ fontWeight: 700, color: row.remaining <= 0 ? 'blue' : 'red' }}>{fmt(row.remaining <= 0 ? 0 : row.remaining)}</TableCell>
                                        <TableCell>{row.dinank}</TableCell>
                                        <TableCell>{row.method}</TableCell>
                                        <TableCell>
                                            <IconButton onClick={() => handleEdit(row)}><Edit /></IconButton>
                                            <IconButton color="error" onClick={() => handleDelete(row.id)}><Delete /></IconButton>
                                            <IconButton color="primary" onClick={() => handleOpenJamaDialog(row)}><Add /></IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                                {displayed.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={10} align="center">नोंदी सापडल्या नाहीत</TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
                        <Pagination page={page} count={pageCount} onChange={(e, v) => setPage(v)} />
                    </Box>
                </>
            )}

            {/* Jama Dialog */}
            <Dialog open={openJamaDialog} onClose={() => { setOpenJamaDialog(false); setJamaForm({ id: null, udhariId: '', rakkam: '', dinank: '', haste: '', method: 'Cash' }); }} fullWidth maxWidth="sm">
                <DialogTitle>जमा करा / इतिहास</DialogTitle>

                <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <TextField size="small" label="रक्कम" type="number" value={jamaForm.rakkam} onChange={(e) => setJamaForm({ ...jamaForm, rakkam: e.target.value })} />
                    <TextField size="small" label="दिनांक" type="date" InputLabelProps={{ shrink: true }} value={jamaForm.dinank} onChange={(e) => setJamaForm({ ...jamaForm, dinank: e.target.value })} />
                    <TextField size="small" label="हस्ते" value={jamaForm.haste} onChange={(e) => setJamaForm({ ...jamaForm, haste: e.target.value })} />
                    <Select size="small" value={jamaForm.method} onChange={(e) => setJamaForm({ ...jamaForm, method: e.target.value })}>
                        <MenuItem value="Cash">Cash</MenuItem>
                        <MenuItem value="Online">Online</MenuItem>
                    </Select>

                    <Typography variant="subtitle1">जमा इतिहास</Typography>
                    <Table size="small">
                        <TableBody>
                            {jamaHistory.length > 0 ? jamaHistory.map(h => (
                                <TableRow key={h.id}>
                                    <TableCell>{fmt(h.rakkam)}</TableCell>
                                    <TableCell>{h.dinank}</TableCell>
                                    <TableCell>{h.haste}</TableCell>
                                    <TableCell>{h.method || '-'}</TableCell>
                                    <TableCell>
                                        <IconButton color="primary" onClick={() => setJamaForm({ id: h.id, udhariId: h.udhariId, rakkam: h.rakkam, dinank: h.dinank, haste: h.haste, method: h.method || 'Cash' })}><Edit fontSize="small" /></IconButton>
                                        <IconButton color="error" onClick={() => handleDeleteJama(h.id, h.udhariId)}><Delete fontSize="small" /></IconButton>
                                    </TableCell>
                                </TableRow>
                            )) : (
                                <TableRow>
                                    <TableCell colSpan={5} align="center">इतिहास नाही</TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </DialogContent>

                <DialogActions>
                    <Button onClick={() => { setOpenJamaDialog(false); setJamaForm({ id: null, udhariId: '', rakkam: '', dinank: '', haste: '', method: 'Cash' }); }}>Close</Button>
                    <Button variant="contained" onClick={handleSaveJama}>{jamaForm.id ? 'अपडेट करा' : 'जतन करा'}</Button>
                </DialogActions>
            </Dialog>

            <ToastContainer position="top-right" autoClose={2000} />
        </Box>
    );
};

export default UsaneGhene;
